// The pure half of the lens — the parts that need no store and no React, so the
// SERVER can call them.
//
// ⚠️ THIS FILE EXISTS BECAUSE OF A RUNTIME-ONLY BUG, and the bug is invisible to
// the compiler. `visibleBrandsFor` used to live in components/persona-context.tsx,
// which is "use client". app/layout.tsx is a SERVER component, and importing a
// plain function out of a client module into a server component typechecks
// perfectly — then throws `visibleBrandsFor is not a function` on every request,
// because a client module's exports arrive server-side as client references, not
// as callable code. Only a browser hitting the page finds it.
//
// So: anything both sides call lives here, in a module with no "use client" and
// no store import. persona-context.tsx re-exports these, so client callers are
// unchanged.
//
// scopedScriptsFor deliberately does NOT live here — it reads the client script
// store, so it is client-only by nature.

import type { Persona, BrandRef } from "@/lib/persona-types";

/**
 * Brand SLUGS this persona may read.
 *
 * admin → every brand; am → their pod's; creator, creative_specialist and
 * brand → the brands they are staffed onto (a brand user's are the brands they
 * manage, usually one); am_ops → the brands of the AMs they cover, already on
 * the persona.
 *
 * Deliberately NOT derived from the scripts. Deriving it would show an AM a brand
 * only while some creator happened to hold a script for it — so a brand with
 * nobody staffed on it yet, which is exactly the brand whose guide and schedule
 * someone still has to write, would be the one brand invisible to the person
 * whose job that is.
 */
export function visibleBrandsFor(p: Persona, allBrands: BrandRef[] = []): string[] {
  if (p.role === "admin") return allBrands.map((b) => b.slug);
  // A copy, every time: a caller's .push() must not rewrite their pod. A WRITER
  // reads their creator's list, already copied onto the persona — they see the
  // brands whose work they write, which is the same set by construction.
  if (
    p.role === "am" ||
    p.role === "am_ops" ||
    p.role === "creative_specialist" ||
    p.role === "creator" ||
    p.role === "writer" ||
    p.role === "brand"
  )
    return [...p.brands];
  // A CREATOR EXPERT READS NO BRAND-SCOPED SURFACE, so the honest answer is
  // none. Their two pages are agency-wide (see persona-types.ts), so nothing
  // downstream consults this; the day something does, an empty list is the end
  // to be wrong on.
  //
  // (An unstaffed brand user gets [] from the list above, never [""]. The brand
  // branch once handed back a one-item list holding the empty string, on the
  // reasoning that it is "a slug that matches nothing". It is not: a script
  // written with no brand carries exactly that slug, so "" is a slug that
  // matches the app's broken rows.)
  return [];
}

/** The two ids a script is scoped by. A `ScriptEntry`, a stored `draft` row, or the halves of a week key. */
export interface ScriptScopeKey {
  brandSlug: string;
  creatorEmail: string;
}

/**
 * MAY THIS PERSONA READ THIS SCRIPT? — the rule, said once, for one script.
 *
 *   admin   → everything
 *   am      → every script for a brand in their pod, whoever wrote it
 *   am_ops  → the same, over the brands of every AM they cover
 *   creative_specialist → the same, over the brands they were picked for
 *   brand   → every script for a brand they manage, whoever wrote it
 *   creator → their own scripts, whichever brand
 *   writer  → their creator's scripts
 *
 * A NARROWED creator or writer (`scopeBrands` set: a staff seat's lens) adds a
 * second condition: the script's brand is one of `scopeBrands`. Absent, the
 * email decides alone, exactly as for a real creator or an admin's lens.
 *
 * ⚠️ THREE CALLERS ASK IT AND MUST NEVER DISAGREE. `scopedScriptsFor` filters
 * the Scripts page with it; the editor asks it of the one script an `?id=`
 * names, so a script outside the lens reads exactly as one that does not exist;
 * and the server asks it of every script write (lib/server/script-write-scope.ts),
 * because a page that hides a script and a POST that still changes it is a gate
 * in the UI only. A second copy of this switch anywhere is the day the three
 * stop agreeing. The PERSONA is the LENS everywhere it is asked: an admin
 * looking through an AM's lens is that AM here, as on every other page.
 *
 * Pure — no store, no React — so the server can call it (see this file's header).
 *
 * ⚠️ EVERY BRANCH COMPARES IDS, and refuses an empty one. A filter on a name
 * would work until somebody edited their Slack profile, then match nothing.
 * And an empty id on the persona is not "matches nothing": `""` is the slug on
 * every script written with no brand, so `"" === ""` would hand a brandless
 * brand user the app's orphaned scripts — the same absent-equals-absent shape
 * as canModifyComment's trap.
 */
export function scriptInScope(p: Persona, s: ScriptScopeKey): boolean {
  switch (p.role) {
    case "admin":
      return true;
    case "am":
    case "am_ops":
    case "creative_specialist":
    case "brand":
      // A brand user with no brand carries `brands: []`, which matches nothing;
      // the `!== ""` refuses the orphaned scripts even if a "" ever got into a
      // list — see the ⚠️ above.
      return s.brandSlug !== "" && p.brands.includes(s.brandSlug);
    case "creator_expert":
      // NONE, AND NOT BECAUSE OF A FILTER. This role has no Scripts page at all
      // (/scripts is on no list of theirs). Said, so the switch is whole and an
      // unhandled role cannot fall out as `undefined`.
      return false;
    case "creator":
      return p.email !== "" && s.creatorEmail === p.email && withinScopeBrands(p.scopeBrands, s.brandSlug);
    case "writer":
      // THE CREATOR'S EMAIL, NOT THE WRITER'S. A writer owns no scripts — they
      // work on someone else's — so filtering on p.email would match nothing and
      // render an empty Scripts page with no error anywhere to say why.
      return p.creatorEmail !== "" && s.creatorEmail === p.creatorEmail && withinScopeBrands(p.scopeBrands, s.brandSlug);
  }
}

/**
 * Does a narrowed lens reach this brand? Always, when nothing narrows it.
 *
 * ⚠️ NEVER THE EMPTY SLUG, even on a narrowed lens: a brandless script belongs
 * to no brand the viewer shares with the creator, so the narrowing cannot vouch
 * for it, whatever a bad list holds.
 */
function withinScopeBrands(scopeBrands: readonly string[] | undefined, slug: string): boolean {
  return scopeBrands === undefined || (slug !== "" && scopeBrands.includes(slug));
}

/**
 * WHOSE DESK IS THIS PERSONA SITTING AT? — the seam the writer role hangs off.
 *
 * A creator sits at their own; a writer sits at the creator's they were hired
 * for; nobody else sits at one at all. Every surface that WRITES — the new-week
 * button, a minted draft's creatorEmail and creatorName, the editor's "my most
 * recent week" fallback, the schedule key a week's dates hang under — asks this
 * instead of asking `persona.role === "creator"`, because the answer to "whose
 * work is this" and the answer to "who is typing" stopped being the same
 * question the day a writer existed.
 *
 * ⚠️ IT RETURNS THE CREATOR, NEVER THE VIEWER. A writer's own email must never
 * reach a draft, a week_date key or a script filter: it matches no script, and
 * a filter that matches nothing renders an empty page rather than an error. The
 * writer's email is for authorship — a comment's authorId, a grade's requester
 * — and for nothing that scopes.
 *
 * `null` is "this persona does not write" — an AM, a Brand, and an admin, who
 * reaches a creator's desk by putting on their lens rather than by owning one.
 */
export interface CreatorSeat {
  /** The CREATOR's email. The id every script, plan and week date is keyed by. */
  email: string;
  /** The CREATOR's name. A label — for a minted draft's creatorName. */
  displayName: string;
}

export function creatorSeatFor(p: Persona): CreatorSeat | null {
  if (p.role === "creator") return { email: p.email, displayName: p.displayName };
  if (p.role === "writer") return { email: p.creatorEmail, displayName: p.creatorName };
  return null;
}

/**
 * IS THIS ROLE SITTING IN THE AM'S SEAT? — `am`, and the three roles that hold
 * an AM's keys without being one.
 *
 * ⚠️ IT IS ONE FACT, SAID ONCE, and it used to be said seven times as
 * `role === "am" || role === "am_ops" || role === "admin"` — in the stage
 * model, the stage buttons, five branches of the editor rollup, and two gates in
 * persona-context. Seven copies of one sentence is seven places to forget the
 * next role, and the symptom of forgetting one is not a crash: it is a reviewer
 * who silently cannot approve.
 *
 * AM OPS reviews across the pods they cover with exactly an AM's powers. A
 * CREATIVE SPECIALIST does the same on the brands they were picked for, one by
 * one — an AM's powers without an AM's pod, copied as a starting point. An
 * ADMIN on the Scripts page simply IS an AM — there is no lens to put on first,
 * and nothing on that page an admin can do that an AM cannot. So everything
 * downstream is written for `am` alone and asks this, rather than listing four
 * roles and hoping the lists agree.
 *
 * ⚠️ AND IT IS ONLY ABOUT THE SCRIPT SURFACES. Which PAGES these four roles
 * have is a different question with a different answer — one table, in
 * lib/route-access.ts — and they do not all share it: an admin has Settings and
 * an AM does not. Sitting in the same seat on the Scripts page is not being the
 * same role. And it is not about OWNING A POD, which is the AM's alone: a brand's
 * lead is an `am`, never anybody this answers yes for.
 *
 * The audit is unaffected: actor_role is the real lens, never this collapse.
 */
export function sitsInAmSeat(role: Persona["role"]): boolean {
  return role === "am" || role === "am_ops" || role === "creative_specialist" || role === "admin";
}

/**
 * WHO IS NOTHING WITHOUT A BRAND — the roles whose entire scope is the brands
 * they are staffed onto, and who therefore may not be saved holding none.
 *
 * It is a data-integrity rule before it is a permissions one. A creator with no
 * brand does not get a smaller app, they get a broken one: every script they
 * write is stamped with an empty brandSlug, and an empty brandSlug matches no
 * brand pill and no brand filter anywhere — so the work is invisible to them, to
 * their AM and to the brand it was written for, with nothing on any page saying
 * why. That is the bad data an admin now has to repair by hand
 * (lib/week-reassign.ts); this is the tap it came out of.
 *
 * A `brand` user is in the list for a different reason than the creator, and
 * ends up in the same place. Their brands are the whole of what they can see —
 * the Scripts page alone, over the brands they manage — so one saved with none
 * opens an app with nothing in it and nothing on it to say why. So the two roles
 * that must never be saved brandless are these two.
 *
 * Not `writer`, who holds no brands at all — theirs are their creator's, copied
 * at persona time. Not `am_ops`, for the same reason one level up: theirs are
 * their AMs'. An `admin` sees every brand there is.
 *
 * ⚠️ AND NOT `am`, NOT ANY MORE. An AM used to be staffed onto brands like a
 * creator; they are not any more. A brand is in a POD and an AM leads one, so
 * an AM's brands are their pod's — a fact set on the BRAND's row, not on
 * theirs. An AM whose pod owns
 * nothing is the ops user's case, not the creator's: an empty app, not a broken
 * one. Nothing they write is stamped with an empty brandSlug, because an AM
 * writes nothing — which was the whole argument for having them here.
 *
 * NOR `creative_specialist`, for the AM's reason: they hold brands like a
 * creator, but they write no scripts, so one on no brand opens an empty app and
 * stamps nothing with an empty brandSlug.
 */
export function roleNeedsBrand(role: Persona["role"]): boolean {
  return role === "creator" || role === "brand";
}

/**
 * WHO HOLDS NO BRANDS OF THEIR OWN — the roles whose scope is read THROUGH
 * something else at persona time, and who may therefore never have a
 * `user_brands` row at all.
 *
 * A WRITER's brands are their creator's. AN OPS USER's are their AMs'. AN AM's
 * are their POD's. NOT
 * a Creative Specialist, whose brands are exactly their own rows. In
 * every one of the three the row would be a SECOND answer to "which brands?",
 * and a second answer to that question is not redundancy, it is a bug waiting
 * for the day the two disagree: the roster page would show one set and every
 * scoped surface would use the other, with nothing anywhere saying why.
 *
 * So it is the rule the roster's forms are drawn from — no brand control on any
 * of the three — AND the rule its actions refuse on, because a server action is
 * a POST endpoint with a generated URL and the form is only a suggestion.
 */
export function roleHoldsNoBrands(role: Persona["role"]): boolean {
  // ⚠️ A CREATOR EXPERT IS IN THIS LIST FOR A FOURTH REASON, and it is worth
  // naming because it is not the other three's. A writer's, an ops user's and
  // an AM's brands are all read THROUGH something else, so a row here would be
  // a second answer to one question. A Creator Expert has no first answer: both
  // of their pages are agency-wide, so a `user_brands` row for them would be a
  // scope nothing reads at all — staffed today, quietly load-bearing the day
  // somebody wires a filter to it, and wrong in a way no page would show.
  return role === "writer" || role === "am_ops" || role === "am" || role === "creator_expert";
}

/**
 * WHAT A LENS ACTUALLY IS, as opposed to what it is called: an email for a
 * person, a slug for a brand, and the role itself for Admin, which is a
 * capability rather than anybody.
 *
 * Two colleagues may share a display name. Nothing forbids it, and Slack
 * overwrites a person's name on every sign-in, so it can happen without anyone
 * choosing it — which is why identity is compared on this and never on the label
 * beside it. The persona switcher also SHOWS this, under the entries whose names
 * would otherwise read alike: picking the wrong lens means reading somebody
 * else's work, and doing everything you then do scoped to them.
 *
 * A brand is keyed by SLUG, not by the contact's email: a brand lens exists per
 * brand and only borrows an email if some person happens to hold that account.
 * Key it by email and every contactless brand collides with every other (all of
 * them carry ""), as do two brands sharing one contact — which, since a brand
 * user may manage several, is now a contact the roster can actually have.
 *
 * An admin's brand lens holds exactly one brand, so its key is that slug. A
 * signed-in brand user may hold more; their key is the list, comma-joined (no
 * slug holds a comma), which equals no single-brand lens — they are not the
 * same view.
 */
export function personaKey(p: Persona): string {
  if (p.role === "admin") return "admin";
  if (p.role === "brand") return p.brands.join(",");
  return p.email;
}

/**
 * Same person, same lens — compared on ids, never on the names beside them.
 *
 * A NARROWED LENS IS NOT ITS CREATOR. Jay narrowed to one brand and Jay whole
 * share an email, and so a `personaKey`; they are different views, so the
 * narrowing is compared too. It stays out of `personaKey` itself, which names
 * the lens in the switcher and its test ids, where one roster never offers both.
 */
export function personaEquals(a: Persona, b: Persona): boolean {
  return a.role === b.role && personaKey(a) === personaKey(b) && scopeKey(a) === scopeKey(b);
}

/** A narrowed lens's brands, in a comparable form; "" for one that is not narrowed. */
function scopeKey(p: Persona): string {
  return (p.role === "creator" || p.role === "writer") && p.scopeBrands ? `scope:${[...p.scopeBrands].sort().join(",")}` : "";
}

/** A brand slug's label. Falls back to the slug — an archived brand still renders as something. */
export function brandLabelIn(brands: BrandRef[], slug: string): string {
  return brands.find((b) => b.slug === slug)?.displayName ?? slug;
}

/**
 * WHOSE SLUG A BROWSER MAY HOLD — the people this IDENTITY can already see, by
 * address. `"all"` for an admin, who may look through any lens.
 *
 * A slug is a person's name in a URL, and the client needs one to write a pick
 * into the address bar and to read it back. It is handed only the slugs of
 * people it is already shown:
 *
 *   admin          → everyone
 *   am, am_ops,
 *   creative_specialist,
 *   brand          → themselves, and every creator on a script in a brand they
 *                    can see — the creator pills the Videos page draws for them
 *   am_ops and
 *   creative_specialist,
 *   also           → the leads of the pods their brands are in (`podLeads`),
 *                    because their pod tier is a pick and `?pod=` is a lead's slug
 *   creator        → themselves
 *   writer         → themselves and the creator whose desk they sit at
 *   creator_expert → themselves
 *
 * ⚠️ THE IDENTITY, NEVER THE LENS. A lens moves without a server round-trip
 * and must find every slug already there: "all" for an admin, and a staff
 * seat's own roster (`lensPeople`) for them.
 *
 * The brand branch follows scopedScriptsFor's own rule: a brand user with no
 * brand has `visibleBrands` empty and so names nobody else, rather than matching
 * the scripts written with no brand at all.
 *
 * A STAFF SEAT ALSO HOLDS THE PEOPLE ON THEIR OWN LENS ROSTER (`lensPeople`,
 * activeLensFor's `switchable`): the writers of their creators above all, whom
 * no script names, and whose lens the seat can put on and must be able to put
 * in an address. Nobody else holds a roster with anybody on it.
 *
 * `podLeads` is the leads of the pods over `visibleBrands` (the layout's
 * scopePodsFor). Only AM Ops and a Creative Specialist are handed them, the two
 * roles whose brands can span pods: an AM's own pod is plain text and needs no
 * slug, and nobody else has a pod tier at all.
 */
export function peopleVisibleTo(
  identity: Persona,
  scripts: readonly { creatorEmail: string; brandSlug: string }[],
  visibleBrands: readonly string[],
  podLeads: readonly string[] = [],
  lensPeople: readonly string[] = [],
): "all" | string[] {
  if (identity.role === "admin") return "all";
  const out = new Set<string>([identity.email]);
  if (identity.role === "am" || identity.role === "am_ops" || identity.role === "creative_specialist") {
    for (const email of lensPeople) out.add(email);
  }
  if (identity.role === "writer") out.add(identity.creatorEmail);
  if (identity.role === "am_ops" || identity.role === "creative_specialist") for (const lead of podLeads) out.add(lead);
  if (
    identity.role === "am" ||
    identity.role === "am_ops" ||
    identity.role === "creative_specialist" ||
    identity.role === "brand"
  ) {
    const brands = new Set(visibleBrands);
    for (const s of scripts) if (s.creatorEmail && brands.has(s.brandSlug)) out.add(s.creatorEmail);
  }
  out.delete("");
  return [...out].sort();
}
